import {CellContext, ColumnDef, ColumnHelper, ColumnMeta, ColumnSort} from "@tanstack/react-table";
import property from "lodash/property";
import {ReactNode} from "react";

export const TABLE_VARIANTS = ["default", "square"] as const;
export type TableVariant = (typeof TABLE_VARIANTS)[number];

export enum TextAlignment {
  Start = "start",
  Center = "center",
  End = "end",
}

export enum SortingDirection {
  Asc = "asc",
  Desc = "desc",
}

export interface Sorting {
  column: string;
  direction: SortingDirection;
}

export type ColumnSpec<T> = {
  fieldName: string;
  fieldTitle?: ReactNode;
  enableSorting?: boolean;
  display?: (cellContext?: CellContext<T, unknown>) => ReactNode; // arbitrary content
  accessor?: (a: T) => ReactNode; // render cell based on data
  size?: number; // px; falls back to the 150px default. Recommended on pinned columns so sticky offsets match an intentional width
  meta?: ColumnMeta<T, unknown>;
};

export const toInnerSorting = (sorting?: Sorting): ColumnSort[] => {
  if (!sorting?.column) return [];

  return [{id: sorting.column, desc: sorting.direction === SortingDirection.Desc}];
};

// flexRender treats any function cell as a component type, so this must keep a stable
// identity across renders or React remounts every cell subtree (wiping cell state)
const accessorCell = (info: {getValue: () => string}) => info.getValue();

// eslint-disable-next-line typescript/no-explicit-any
export const specToDef = <T>(columnHelper: ColumnHelper<T>, s: ColumnSpec<T>): ColumnDef<T, any> => {
  const header =
    s.fieldTitle == null || typeof s.fieldTitle === "string" ? (s.fieldTitle ?? undefined) : () => s.fieldTitle;

  if (s.display) {
    return columnHelper.display({
      id: s.fieldName,
      header,
      cell: s.display,
      enableSorting: s.enableSorting ?? false,
      size: s.size,
      meta: s.meta,
    });
  } else if (Object.hasOwn(s, "accessor")) {
    return {
      cell: accessorCell,
      id: s.fieldName,
      header,
      enableSorting: s.enableSorting ?? false,
      accessorFn: s.accessor,
      size: s.size,
      meta: s.meta,
    };
  }
  return columnHelper.accessor(property(s.fieldName), {
    header,
    id: s.fieldName,
    enableSorting: s.enableSorting ?? false,
    size: s.size,
    meta: s.meta,
  });
};
